import { gql } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';

export const GOAL_RULE_FIELDS = `#graphql
  name
  timeHorizon
  targetAmountCents
  householdClientGroup { id name }
`;

export const ACCOUNT_RULE_FIELDS = `#graphql
  type
  applyForGovFunds
  jurisdiction
  sourceOfFunds
  repCode { code }
  user { physicalAddress { country city province streetName postal unitNumber houseNumber neighborhood } maritalStatus countryOfTaxResidence }
  baseCurrency
  availableCurrencies
`;

export const ENTITY_RULE_FIELDS = `#graphql
  firstName
  middleName
  lastName
  primaryEmail
  phone
  dateOfBirth
  language
  preferredMethodOfCommunication
  inProvinceSince
  gender
  physicalAddress { country city province streetName postal unitNumber houseNumber neighborhood }
`;

const enumerateCustomFieldsQuery = (fields?: string[]) => {
  if (!fields) return '';
  return `
    customFields(keys: ${JSON.stringify(fields)}) {
      key
      value
      customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
      selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
    }
  `;
};

export const FETCH_USER_CUSTOM_FIELDS = (
  derivedCustomFields?: string[],
  enableCustodianCustomFields?: boolean,
) => gql`
  query fetchUserCustomFields($id: SafeString!) {
    fetch:fetchUser(input: { userId: $id }) {
      object:user {
        id: stringId
        ${enumerateCustomFieldsQuery(derivedCustomFields)}
        ${enableCustodianCustomFields ? 'custodianCustomFields { key value }' : ''}
        ${ENTITY_RULE_FIELDS}
      }
    }
  }
`;

export const FETCH_GOAL_CUSTOM_FIELDS = (
  derivedCustomFields?: string[],
  _enableCustodianCustomFields?: any,
) => gql`
  query fetchGoalCustomFields($id: ObjectID!) {
    fetch:fetchGoal(goalId: $id) {
      object:goal {
        id
        ${enumerateCustomFieldsQuery(derivedCustomFields)}
        ${GOAL_RULE_FIELDS}
      }
    }
  }
`;

export const FETCH_ACCOUNT_CUSTOM_FIELDS = (
  derivedCustomFields?: string[],
  enableCustodianCustomFields?: boolean,
) => gql`
  query fetchAccountCustomFields($id: SafeString!) {
    fetch:fetchAccount(input: { accountId: $id }) {
      object:account {
        id: stringId
        ${enumerateCustomFieldsQuery(derivedCustomFields)}
        ${enableCustodianCustomFields ? 'custodianCustomFields { key value }' : ''}
        ${ACCOUNT_RULE_FIELDS}
      }
    }
  }
`;

export const UPDATE_USER_CUSTOM_FIELDS = gql`
  mutation updateUserCustomFields(
    $id: ID!
    $newCustomFields: [CustomFieldInput]!
  ) {
    updateUser(input: { userId: $id, customFields: $newCustomFields }) {
      user {
        id
      }
    }
  }
`;

export const UPDATE_GOAL_CUSTOM_FIELDS = gql`
  mutation updateGoalCustomFields(
    $id: ObjectID!
    $newCustomFields: [CustomFieldInput]!
  ) {
    updateGoal(input: { goalId: $id, customFields: $newCustomFields }) {
      goal {
        id
      }
    }
  }
`;

export const UPDATE_ACCOUNT_CUSTOM_FIELDS = gql`
  mutation updateAccountCustomFields(
    $id: ObjectID!
    $newCustomFields: [CustomFieldInput]!
  ) {
    updateAccount(input: { accountId: $id, customFields: $newCustomFields }) {
      account {
        id
      }
    }
  }
`;

export const FETCH_ACCOUNT_GROUP_CUSTOM_FIELDS = (
  derivedCustomFields?: string[],
) => gql`
  query fetchAccountGroupCustomFields($id: ObjectID!) {
    fetch: fetchAccountGroup(accountGroupId: $id) {
      object: accountGroup {
        id
        ${enumerateCustomFieldsQuery(derivedCustomFields)}
      }
    }
  }
`;

export const UPDATE_ACCOUNT_GROUP_CUSTOM_FIELDS = gql`
  mutation updateAccountGroupCustomFields(
    $id: ObjectID!
    $newCustomFields: [CustomFieldInput]!
  ) {
    updateAccountGroup(input: { accountGroupId: $id, customFields: $newCustomFields }) {
      accountGroup {
        id
      }
    }
  }
`;

export const FETCH_HOUSEHOLD_CUSTOM_FIELDS = (
  derivedCustomFields?: string[],
) => gql`
  query fetchHouseholdCustomFields($id: SafeString!) {
    fetch: fetchClientGroup(input: { clientGroupId: $id }) {
      object: clientGroup {
        id: stringId
        ${enumerateCustomFieldsQuery(derivedCustomFields)}
      }
    }
  }
`;

export const UPDATE_HOUSEHOLD_CUSTOM_FIELDS = gql`
  mutation updateHouseholdCustomFields(
    $id: ObjectID!
    $newCustomFields: [CustomFieldInput]!
  ) {
    updateClientGroup(input: { clientGroupId: $id, customFields: $newCustomFields }) {
      clientGroup {
        id
      }
    }
  }
`;

export const UPDATE_CUSTODIAN_CUSTOM_FIELDS = gql`
  mutation UpdateCustodianCustomFields(
    $input: UpdateCustodianCustomFieldsInput!
  ) {
    updateCustodianCustomFields(input: $input) {
      custodianCustomFields {
        key
        value
      }
    }
  }
`;
